import { setIn } from 'timm';
import { all, call } from 'typed-redux-saga';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  SagaGenerator,
  setupSlice,
} from '@owl-frontend/redux';
import * as filesApi from '../../api/files/client';
import { FILE_CONTENT_TIMEOUT_LIMIT } from '../../api/files/client';
import type { ClaimFile } from '../../api/files/interface';

function* deleteFiles(action: {
  payload: { dossierId: string; fileIds: string[] };
}): SagaGenerator<string[]> {
  const { fileIds } = action.payload;
  yield* all(fileIds.map((fileId) => call(filesApi.deleteFile, fileId)));
  return fileIds;
}

function* updateFileMetadata(action: {
  payload: {
    dossierId: string;
    files: Array<{
      file_id: string;
      tags?: string[];
      fileScope?: string | null;
      title?: string;
    }>;
  };
}): SagaGenerator<void> {
  const { files: filesToUpdate } = action.payload;
  yield* all(
    filesToUpdate.map((f) =>
      call(filesApi.updateFile, f.file_id, {
        tags: f.tags,
        fileScope: f.fileScope,
        title: f.title,
      })
    )
  );
}

export interface FilesState {
  files: {
    [dossier_id: string]: Record<string, ClaimFile>;
  };
  paginatedFileIds: {
    [dossier_id: string]: string[];
  };
  fileFilters: {
    [dossierId: string]: { tags: string[]; scopes: string[] };
  };
  filesCount: {
    [dossier_id: string]: number;
  };
  unsupportedFilesCount: {
    [dossier_id: string]: number;
  };
  unsupportedFileTypes: {
    [dossier_id: string]: string[];
  };
  filesContent: {
    [fileId: string]: {
      [pageNumber: string]: {
        url: string;
        requestedDate: number;
      };
    };
  };
  filesOCR: {
    [fileId: string]: {
      [dataVersion: string]: {
        [pageNumber: string]: string;
      };
    };
  };
  actions: {
    files: AsyncActionState<ClaimFile[]>;
    fetchFile: AsyncActionState<ClaimFile>;
    fetchFileContents: AsyncActionState;
    fetchFilesOCR: AsyncActionState;
    fetchFileSource: AsyncActionState<{ url: string }>;
    deleteFiles: AsyncActionState;
    updateFileMetadata: AsyncActionState;
    fetchFileFilters: AsyncActionState;
  };
}

const initialState: FilesState = {
  files: {},
  paginatedFileIds: {},
  filesCount: {},
  unsupportedFilesCount: {},
  unsupportedFileTypes: {},
  filesContent: {},
  filesOCR: {},
  fileFilters: {},
  actions: {
    files: { status: 'uninitialized' },
    fetchFile: { status: 'uninitialized' },
    fetchFileContents: { status: 'uninitialized' },
    fetchFilesOCR: { status: 'uninitialized' },
    fetchFileSource: { status: 'uninitialized' },
    deleteFiles: { status: 'uninitialized' },
    updateFileMetadata: { status: 'uninitialized' },
    fetchFileFilters: { status: 'uninitialized' },
  },
};

const slice = setupSlice('files', initialState)
  .addAsyncSagas({
    files: invoke(filesApi.fetchFiles),
    fetchFile: invoke(filesApi.fetchFile),
    fetchFileFilters: invoke(filesApi.fetchFileFilters),
    downloadFile: invoke(filesApi.downloadFile),
    deleteFiles,
    fetchFileSource: invoke(filesApi.fetchFileSource),
  })
  .addAsyncSagas({ updateFileMetadata }, { timeoutLimit: -1 })
  .addAsyncSagas(
    {
      fetchFileContents: invoke(filesApi.fetchFilePage),
      fetchFilesOCR: invoke(filesApi.fetchFilesOCR),
    },
    {
      timeoutLimit: FILE_CONTENT_TIMEOUT_LIMIT,
    }
  )
  .addReducers({
    'files/fulfilled': (state, action) => {
      const dossier_id = action.meta.arg.dossier_id;
      const offset = action.meta.arg.offset;

      const newFiles = action.payload.files.reduce((acc, file) => {
        acc[file.file_id] = file;
        return acc;
      }, {} as Record<string, ClaimFile>);

      const newFileIds = action.payload.files.map((file) => file.file_id);

      if (!offset) {
        state.files[dossier_id] = newFiles;
        state.paginatedFileIds[dossier_id] = newFileIds;
      } else {
        state.files[dossier_id] = {
          ...state.files[dossier_id],
          ...newFiles,
        };
        const existingIds = state.paginatedFileIds[dossier_id] ?? [];
        const existingIdSet = new Set(existingIds);
        state.paginatedFileIds[dossier_id] = [
          ...existingIds,
          ...newFileIds.filter((id) => !existingIdSet.has(id)),
        ];
      }
      state.filesCount[dossier_id] = action.payload.count;
      if (action.payload.unsupported_files != null) {
        state.unsupportedFilesCount[dossier_id] =
          action.payload.unsupported_files;
      }
      if (action.payload.unsupported_file_types != null) {
        state.unsupportedFileTypes[dossier_id] =
          action.payload.unsupported_file_types;
      }
    },
    'fetchFile/fulfilled': (state, action) => {
      const dossier_id = action.meta.arg.dossier_id;
      const file_id = action.meta.arg.file_id;
      state.files[dossier_id] = {
        ...state.files[dossier_id],
        [file_id]: action.payload,
      };
    },
    'fetchFileFilters/fulfilled': (state, action) => {
      const dossier_id = action.meta.arg.dossier_id;
      state.fileFilters[dossier_id] = action.payload;
    },
    'fetchFileContents/fulfilled': (state, action) => {
      const pages = action.payload.pages;
      for (const payload of pages) {
        state.filesContent = setIn(
          state.filesContent,
          [action.meta.arg.fileId, payload.page],
          {
            url: payload.url,
            requestedDate: new Date().getTime(),
          }
        ) as typeof state.filesContent;
      }
    },
    'fetchFileSource/fulfilled': (state, action) => {
      state.filesContent = setIn(
        state.filesContent,
        [action.meta.arg.fileId, 1],
        {
          url: action.payload.url,
          requestedDate: new Date().getTime(),
        }
      ) as typeof state.filesContent;
    },
    'fetchFilesOCR/fulfilled': (state, action) => {
      state.filesOCR = setIn(
        state.filesOCR,
        [action.meta.arg.fileId, action.meta.arg.dataVersion],
        action.payload
      ) as typeof state.filesOCR;
    },
    'downloadFile/fulfilled': (state, action) => {
      window.open(action.payload.url);
    },
    'deleteFiles/fulfilled': (state, action) => {
      const { dossierId, fileIds } = action.meta.arg;
      if (state.files[dossierId]) {
        for (const fileId of fileIds) {
          delete state.files[dossierId][fileId];
        }
      }
      if (state.paginatedFileIds[dossierId]) {
        const deletedIdSet = new Set(fileIds);
        state.paginatedFileIds[dossierId] = state.paginatedFileIds[
          dossierId
        ].filter((id) => !deletedIdSet.has(id));
      }
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
